import React from "react";
import { Container } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaArrowRight, FaCheck, FaShieldAlt } from "react-icons/fa";

import { reasons } from "../data/whyChooseUs";

/**
 * Fourth distinct pattern on the home page: a numbered timeline with items
 * alternating either side of a peach spine. Services/Industries are card
 * grids and Technology is a dark band, so a vertical narrative reads as
 * something new — and with only four reasons there's room to surface the
 * `highlights` the home page otherwise never shows.
 */
function WhyChooseUs() {
  return (
    <section className="why">
      <style>{`
        .why {
          --peach-deep: #ff6b35;
          --peach: #ff9066;
          --peach-soft: #f3c09f;
          --peach-tint: #ffe9e0;
          --peach-ink: #c2410c;

          position: relative;
          /* AOS slides these in horizontally; without this the transform
             can push a scrollbar onto the page mid-animation. */
          overflow-x: hidden;
          padding: clamp(60px, 8vw, 104px) 0 clamp(70px, 9vw, 112px);
          background: linear-gradient(to bottom, #ffffff 0%, #fff8f5 45%, #ffffff 100%);
        }
        .why__glow {
          position: absolute; pointer-events: none;
          width: 30rem; height: 30rem; top: 20%; right: -12rem;
          border-radius: 50%; filter: blur(95px); opacity: .5;
          background: radial-gradient(circle, #ffd9c6, transparent 70%);
        }

        .why__badge {
          display: inline-flex; align-items: center; gap: 9px;
          background: var(--peach-tint); color: var(--peach-ink);
          padding: 9px 22px; border-radius: 999px;
          font-weight: 700; font-size: .82rem; letter-spacing: .08em;
        }
        .why__title {
          margin-top: 22px; color: #0f172a; font-weight: 800;
          font-size: clamp(1.8rem, 4vw, 3.1rem);
          line-height: 1.15; letter-spacing: -.02em;
        }
        .why__title em {
          font-style: normal;
          background: linear-gradient(105deg, var(--peach-deep), var(--peach) 60%, var(--peach-soft));
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .why__lead {
          max-width: 720px; margin: 16px auto 0;
          color: #64748b; font-size: clamp(.96rem, 1.6vw, 1.05rem); line-height: 1.85;
        }

        /* --- Timeline --- */
        .why__line { position: relative; margin-top: 54px; }
        /* The spine. Left-hand rail on phones, centred from lg up. */
        .why__line::before {
          content: ""; position: absolute; top: 6px; bottom: 6px;
          left: 23px; width: 3px; border-radius: 999px;
          background: linear-gradient(180deg,
            transparent, var(--peach) 8%, var(--peach-soft) 50%, var(--peach) 92%, transparent);
        }
        @media (min-width: 992px) {
          .why__line::before { left: 50%; transform: translateX(-50%); }
        }

        .why-item { position: relative; padding-left: 76px; margin-bottom: 28px; }
        .why-item:last-child { margin-bottom: 0; }

        .why-item__dot {
          position: absolute; left: 0; top: 4px;
          width: 48px; height: 48px; border-radius: 50%;
          display: grid; place-items: center;
          font-weight: 800; font-size: 1.05rem; color: #0f172a;
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          /* Ring punches the spine out from behind the number */
          box-shadow: 0 0 0 7px #fff8f5, 0 8px 20px rgba(255,107,53,.4);
          z-index: 1;
        }

        /* Must follow the base .why-item__dot rule: a media query adds no
           specificity, so source order is what lets position:static win and
           hand the dot back to the grid for vertical centring. */
        @media (min-width: 992px) {
          .why-item {
            padding-left: 0; margin-bottom: 20px;
            display: grid; grid-template-columns: 1fr 104px 1fr; align-items: center;
          }
          /* Both children share row 1 explicitly. Without this, a right-hand
             card claims column 3 first and sparse auto-placement refuses to
             step back to column 2, dropping the dot into a second row. */
          .why-item > * { grid-row: 1; }
          .why-item--left  .why-item__card { grid-column: 1; }
          .why-item--right .why-item__card { grid-column: 3; }
          .why-item__dot { grid-column: 2; justify-self: center; position: static; }
        }

        .why-item__card {
          display: block; text-decoration: none;
          padding: 24px; border-radius: 20px;
          background: #fff; border: 1px solid #f2e6df;
          box-shadow: 0 6px 22px rgba(194,65,12,.07);
          transition: transform .26s ease, box-shadow .26s ease, border-color .26s ease;
        }
        .why-item__card:focus-visible {
          outline: 3px solid var(--peach-deep); outline-offset: 3px;
        }
        .why-item__head {
          display: flex; align-items: center; gap: 13px; margin-bottom: 12px;
        }
        .why-item__icon {
          width: 50px; height: 50px; border-radius: 14px; flex-shrink: 0;
          display: grid; place-items: center;
          background: var(--peach-tint); color: var(--peach-deep);
          transition: background .26s ease, color .26s ease, transform .26s ease;
        }
        .why-item__title {
          margin: 0; color: #0f172a; font-weight: 700; font-size: 1.12rem;
        }
        .why-item__text {
          color: #64748b; font-size: .93rem; line-height: 1.75; margin-bottom: 14px;
        }
        .why-item__chips {
          display: flex; flex-wrap: wrap; gap: 7px; margin-bottom: 16px;
        }
        .why-item__chip {
          display: inline-flex; align-items: center; gap: 6px;
          background: var(--peach-tint); color: var(--peach-ink);
          border-radius: 999px; padding: 4px 11px;
          font-size: .73rem; font-weight: 600;
        }
        /* flex-shrink:0 — a long label that wraps to two lines otherwise
           compresses the tick, so chips render at inconsistent icon sizes. */
        .why-item__chip svg { font-size: .58rem; flex-shrink: 0; }
        .why-item__more {
          display: inline-flex; align-items: center; gap: 8px;
          color: var(--peach-ink); font-weight: 700; font-size: .9rem;
        }
        .why-item__more svg { transition: transform .26s ease; }

        /* A mouse hovers; a finger can't. On touch, lib/touchMotion.js adds
           .is-live as the card scrolls through the middle of the screen. */
        .why-item__card:hover,
        .why-item__card.is-live {
          transform: translateY(-6px);
          box-shadow: 0 22px 46px rgba(194,65,12,.18);
          border-color: var(--peach-soft);
        }
        .why-item__card:hover .why-item__icon,
        .why-item__card.is-live .why-item__icon {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          color: #fff; transform: scale(1.06);
        }
        .why-item__card:hover .why-item__more svg,
        .why-item__card.is-live .why-item__more svg { transform: translateX(5px); }

        @media (prefers-reduced-motion: reduce) {
          .why-item__card, .why-item__icon, .why-item__more svg { transition: none; }
          .why-item__card:hover { transform: none; }
        }
      `}</style>

      <span className="why__glow" />

      <Container style={{ position: "relative", zIndex: 1 }}>
        <div className="text-center" data-aos="fade-up">
          <span className="why__badge">
            <FaShieldAlt />
            WHY CHOOSE SARVAVYA
          </span>

          {/* theme.css hides <br> in section headings below 576px — the
              explicit space keeps the words apart once it disappears. */}
          <h2 className="why__title">
            Delivering Confidence,
            <br />{" "}
            <em>Innovation &amp; Results</em>
          </h2>

          <p className="why__lead">
            Combining technology expertise, programme delivery, industry
            knowledge, and digital innovation to help organizations achieve
            sustainable business success.
          </p>
        </div>

        <div className="why__line">
          {reasons.map((item, i) => {
            const { Icon } = item;
            const onLeft = i % 2 === 0;
            return (
              <div
                key={item.slug}
                className={`why-item ${onLeft ? "why-item--left" : "why-item--right"}`}
              >
                <Link
                  to={`/why-choose-us/${item.slug}`}
                  className="why-item__card"
                  data-live
                  data-aos={onLeft ? "fade-right" : "fade-left"}
                >
                  <span className="why-item__head">
                    <span className="why-item__icon">
                      <Icon size={22} />
                    </span>
                    <h3 className="why-item__title">{item.title}</h3>
                  </span>

                  <p className="why-item__text">{item.description}</p>

                  {/* Surfaces detail-page content the home page otherwise hides */}
                  <span className="why-item__chips">
                    {item.highlights.slice(0, 3).map((h) => (
                      <span className="why-item__chip" key={h}>
                        <FaCheck />
                        {h}
                      </span>
                    ))}
                  </span>

                  <span className="why-item__more">
                    Learn more <FaArrowRight />
                  </span>
                </Link>

                <span className="why-item__dot" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

export default WhyChooseUs;
